import { useEffect, useMemo, useRef, useState } from "react";
import { AppLayout } from "@/components/layout/AppLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { usePermissions } from "@/context/PermissionsContext";
import { TID } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import {
  ShieldAlert, Folder, FolderOpen, ChevronRight, ChevronDown,
  Building2, MapPin, Home, Layers, Upload, Download, Trash2,
  FileText, AlertCircle, Search, Loader2, CalendarClock,
} from "lucide-react";
import { toast } from "sonner";

// ------------- constants -------------
const DOC_TYPES = [
  { value: "contract",    label: "Contract" },
  { value: "drawing",     label: "Drawing" },
  { value: "om",          label: "O&M Manual" },
  { value: "certificate", label: "Certificate" },
  { value: "report",      label: "Report" },
  { value: "photo",       label: "Photo" },
  { value: "other",       label: "Other" },
];

const TYPE_COLOURS = {
  contract:    "bg-sky-100 text-sky-800",
  drawing:     "bg-violet-100 text-violet-800",
  om:          "bg-amber-100 text-amber-800",
  certificate: "bg-emerald-100 text-emerald-800",
  report:      "bg-slate-100 text-slate-800",
  photo:       "bg-rose-100 text-rose-800",
  other:       "bg-slate-100 text-slate-500",
};

const fmtBytes = (n) => {
  if (n == null) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

const daysUntil = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr + "T00:00:00Z");
  const now = new Date();
  return Math.round((d.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
};

// ------------- main -------------
export default function DocumentsPage() {
  const { can, loaded } = usePermissions();
  const canView = can("documents", "view");
  const canCreate = can("documents", "create");
  const canDelete = can("documents", "delete");

  const [tree, setTree] = useState(null);
  const [stats, setStats] = useState(null);
  const [selected, setSelected] = useState({ kind: "all", id: null, label: "All documents" });
  const [docs, setDocs] = useState([]);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("");
  const [expiringOnly, setExpiringOnly] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);

  const loadTree = async () => {
    try {
      const [{ data: t }, { data: s }] = await Promise.all([
        api.get("/documents/tree"),
        api.get("/documents/stats"),
      ]);
      setTree(t);
      setStats(s);
    } catch (e) {
      console.warn("[Documents] tree load failed", e?.message || e);
    }
  };

  const loadDocs = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (selected.kind === "client")   p.set("client_id", selected.id);
      if (selected.kind === "site")     p.set("site_id", selected.id);
      if (selected.kind === "building") p.set("building_id", selected.id);
      if (selected.kind === "area")     p.set("area_id", selected.id);
      if (selected.kind === "unattached") p.set("unattached", "true");
      if (search)      p.set("q", search);
      if (filterType)  p.set("document_type", filterType);
      if (expiringOnly) p.set("expiring_within_days", "90");
      p.set("limit", "300");
      const { data } = await api.get(`/documents?${p}`);
      setDocs(data);
    } catch (e) {
      console.warn("[Documents] list failed", e?.message || e);
      toast.error("Failed to load documents");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (loaded && canView) loadTree(); /* eslint-disable-next-line */ }, [loaded, canView]);
  useEffect(() => { if (loaded && canView) loadDocs(); /* eslint-disable-next-line */ }, [loaded, canView, selected, filterType, expiringOnly]);

  // debounced search
  useEffect(() => {
    if (!loaded || !canView) return;
    const h = setTimeout(loadDocs, 300);
    return () => clearTimeout(h);
    /* eslint-disable-next-line */
  }, [search]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this document? This cannot be undone.")) return;
    try {
      await api.delete(`/documents/${id}`);
      toast.success("Document deleted");
      loadDocs();
      loadTree();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  };

  if (!loaded) return <AppLayout title="Documents"><div className="h-64 rounded-lg bg-white border border-slate-200 shimmer" /></AppLayout>;
  if (!canView) {
    return (
      <AppLayout title="Documents">
        <div className="max-w-md mx-auto mt-16 text-center">
          <ShieldAlert className="w-6 h-6 text-rose-700 inline-block" />
          <h2 className="font-display text-xl font-semibold text-slate-900 mt-3">Access restricted</h2>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout
      title="Documents"
      subtitle="Client → Site → Building → Area document tree · SharePoint-ready"
      actions={
        canCreate && (
          <Button data-testid={TID.docsUploadOpen} onClick={() => setUploadOpen(true)} className="bg-gbe hover:bg-gbe/90 text-white">
            <Upload className="w-4 h-4 mr-1.5" /> Upload Document
          </Button>
        )
      }
    >
      {/* Stat bar */}
      <StatBar stats={stats} onExpiringToggle={() => setExpiringOnly((v) => !v)} expiringOnly={expiringOnly} />

      <div className="grid grid-cols-12 gap-4 mt-4">
        {/* Tree */}
        <aside className="col-span-4 lg:col-span-3 rounded-lg border border-slate-200 bg-white p-3 max-h-[calc(100vh-260px)] overflow-y-auto">
          <TreeRoot tree={tree} selected={selected} onSelect={setSelected} />
        </aside>

        {/* Docs list */}
        <section className="col-span-8 lg:col-span-9 space-y-3">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                data-testid={TID.docsSearch}
                value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search title, filename, description…"
                className="pl-9"
              />
            </div>
            <Select value={filterType || "__all"} onValueChange={(v) => setFilterType(v === "__all" ? "" : v)}>
              <SelectTrigger data-testid={TID.docsFilterType} className="w-48"><SelectValue placeholder="All types" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">All types</SelectItem>
                {DOC_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center justify-between text-xs text-slate-500">
            <span>Viewing: <b className="text-slate-800">{selected.label}</b> · {docs.length} document{docs.length === 1 ? "" : "s"}</span>
            {expiringOnly && <Badge className="bg-amber-100 text-amber-800">Filtered: expiring ≤ 90 days</Badge>}
          </div>

          <DocsList docs={docs} loading={loading} canDelete={canDelete} onDelete={handleDelete} />
        </section>
      </div>

      <UploadDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        selected={selected}
        tree={tree}
        onUploaded={() => { setUploadOpen(false); loadDocs(); loadTree(); }}
      />
    </AppLayout>
  );
}

// ------------- Stat bar -------------
function StatBar({ stats, onExpiringToggle, expiringOnly }) {
  if (!stats) return null;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
      <StatCard label="Total documents" value={stats.total} icon={FileText} tone="brand" />
      <StatCard label="Expiring ≤ 30 days" value={stats.expiring_within_30_days} icon={CalendarClock} tone={stats.expiring_within_30_days > 0 ? "warn" : "default"} />
      <StatCard label="Expiring ≤ 90 days" value={stats.expiring_within_90_days} icon={CalendarClock}
                tone={stats.expiring_within_90_days > 0 ? "warn" : "default"}
                onClick={onExpiringToggle} highlighted={expiringOnly} />
      <StatCard label="Expired" value={stats.expired} icon={AlertCircle} tone={stats.expired > 0 ? "danger" : "good"} />
      <StatCard label="Review due" value={stats.review_due} icon={CalendarClock} tone={stats.review_due > 0 ? "warn" : "default"} />
    </div>
  );
}

function StatCard({ label, value, icon: Icon, tone = "default", onClick, highlighted }) {
  const tones = {
    default: "text-slate-900", danger: "text-rose-700", warn: "text-amber-700", good: "text-emerald-700", brand: "text-gbe",
  };
  return (
    <button type="button" onClick={onClick} disabled={!onClick}
      className={`text-left rounded-lg border p-3 transition-all ${
        highlighted ? "border-gbe ring-2 ring-gbe/20 bg-white" : "border-slate-200 bg-white hover:border-slate-300"
      } ${onClick ? "cursor-pointer" : "cursor-default"}`}
    >
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
        {Icon && <Icon className="w-3.5 h-3.5 text-slate-400" />}
      </div>
      <div className={`mt-1 font-display text-2xl ${tones[tone]}`}>{value ?? "—"}</div>
    </button>
  );
}

// ------------- Tree -------------
function TreeRoot({ tree, selected, onSelect }) {
  if (!tree) return <div className="text-xs text-slate-400 py-6 text-center">Loading tree…</div>;
  return (
    <div className="text-sm">
      <TreeItem
        icon={Folder} openIcon={FolderOpen}
        label="All documents" count={undefined}
        selected={selected.kind === "all"}
        onClick={() => onSelect({ kind: "all", id: null, label: "All documents" })}
        alwaysOpen
      />
      {tree.unattached_count > 0 && (
        <TreeItem
          icon={Layers} label="Unattached" count={tree.unattached_count} depth={1}
          selected={selected.kind === "unattached"}
          onClick={() => onSelect({ kind: "unattached", id: null, label: "Unattached documents" })}
        />
      )}
      {tree.clients.map((c) => (
        <ClientNode key={c.id} client={c} selected={selected} onSelect={onSelect} />
      ))}
      {tree.clients.length === 0 && (
        <div className="text-xs text-slate-400 mt-2 py-4 text-center">No clients defined yet. Add clients via CRM → Master Data.</div>
      )}
    </div>
  );
}

function ClientNode({ client, selected, onSelect }) {
  const [open, setOpen] = useState(false);
  const hasChildren = client.sites?.length > 0;
  return (
    <>
      <TreeItem
        icon={Building2}
        label={client.name}
        count={client.doc_count}
        depth={1}
        toggle={hasChildren}
        open={open}
        onToggle={() => setOpen((v) => !v)}
        selected={selected.kind === "client" && selected.id === client.id}
        onClick={() => onSelect({ kind: "client", id: client.id, label: client.name })}
        testId={TID.docsTreeNode("client", client.id)}
      />
      {open && client.sites.map((s) => (
        <SiteNode key={s.id} site={s} selected={selected} onSelect={onSelect} />
      ))}
    </>
  );
}

function SiteNode({ site, selected, onSelect }) {
  const [open, setOpen] = useState(false);
  const hasChildren = site.buildings?.length > 0;
  return (
    <>
      <TreeItem
        icon={MapPin} label={site.name} count={site.doc_count} depth={2}
        toggle={hasChildren} open={open} onToggle={() => setOpen((v) => !v)}
        selected={selected.kind === "site" && selected.id === site.id}
        onClick={() => onSelect({ kind: "site", id: site.id, label: site.name })}
        testId={TID.docsTreeNode("site", site.id)}
      />
      {open && site.buildings.map((b) => (
        <BuildingNode key={b.id} building={b} selected={selected} onSelect={onSelect} />
      ))}
    </>
  );
}

function BuildingNode({ building, selected, onSelect }) {
  const [open, setOpen] = useState(false);
  const hasChildren = building.areas?.length > 0;
  return (
    <>
      <TreeItem
        icon={Home} label={building.name} count={building.doc_count} depth={3}
        toggle={hasChildren} open={open} onToggle={() => setOpen((v) => !v)}
        selected={selected.kind === "building" && selected.id === building.id}
        onClick={() => onSelect({ kind: "building", id: building.id, label: building.name })}
        testId={TID.docsTreeNode("building", building.id)}
      />
      {open && building.areas.map((a) => (
        <TreeItem
          key={a.id} icon={Layers} label={a.name} count={a.doc_count} depth={4}
          selected={selected.kind === "area" && selected.id === a.id}
          onClick={() => onSelect({ kind: "area", id: a.id, label: a.name })}
          testId={TID.docsTreeNode("area", a.id)}
        />
      ))}
    </>
  );
}

function TreeItem({ icon: Icon, openIcon, label, count, depth = 0, selected, onClick, toggle, open, onToggle, testId, alwaysOpen }) {
  const DisplayIcon = alwaysOpen && openIcon ? openIcon : Icon;
  return (
    <div
      data-testid={testId}
      className={`flex items-center gap-1 py-1.5 pr-2 rounded cursor-pointer text-sm ${
        selected ? "bg-gbe/10 text-gbe font-medium" : "hover:bg-slate-50 text-slate-700"
      }`}
      style={{ paddingLeft: `${depth * 12 + 4}px` }}
    >
      {toggle ? (
        <button onClick={(e) => { e.stopPropagation(); onToggle(); }} className="text-slate-400 hover:text-slate-700">
          {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        </button>
      ) : (
        <span className="w-3.5 inline-block" />
      )}
      <button onClick={onClick} className="flex-1 flex items-center gap-1.5 text-left min-w-0">
        <DisplayIcon className="w-3.5 h-3.5 shrink-0" />
        <span className="truncate">{label}</span>
      </button>
      {count != null && count > 0 && (
        <Badge className="text-[10px] bg-slate-100 text-slate-600 h-4 min-w-[20px] justify-center">{count}</Badge>
      )}
    </div>
  );
}

// ------------- Doc list -------------
function DocsList({ docs, loading, canDelete, onDelete }) {
  if (loading) return <div className="h-64 rounded-lg bg-white border border-slate-200 shimmer" />;
  if (docs.length === 0) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-10 text-center">
        <FileText className="w-8 h-8 text-slate-300 mx-auto mb-2" />
        <div className="text-sm text-slate-500">No documents in this scope yet.</div>
      </div>
    );
  }
  return (
    <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
      <div className="grid gap-2 px-4 py-2.5 text-[11px] uppercase tracking-wider text-white bg-gbe"
           style={{ gridTemplateColumns: "2fr 0.8fr 1fr 0.9fr 0.9fr 0.6fr" }}>
        <div>Title / File</div>
        <div>Type</div>
        <div>Tags</div>
        <div className="text-right">Expiry</div>
        <div>Uploaded</div>
        <div className="text-right">Actions</div>
      </div>
      <div className="divide-y divide-slate-100">
        {docs.map((d) => {
          const dtil = daysUntil(d.expiry_date);
          const expiryTone = dtil == null ? "text-slate-400"
                            : dtil < 0     ? "text-rose-700 font-semibold"
                            : dtil <= 30   ? "text-amber-700 font-semibold"
                            : dtil <= 90   ? "text-amber-600"
                            : "text-slate-600";
          return (
            <div key={d.id} data-testid={TID.docsRow(d.id)}
                 className="grid gap-2 px-4 py-3 text-sm items-center hover:bg-slate-50"
                 style={{ gridTemplateColumns: "2fr 0.8fr 1fr 0.9fr 0.9fr 0.6fr" }}>
              <div className="min-w-0">
                <div className="text-slate-900 font-medium truncate">{d.title}</div>
                <div className="text-xs text-slate-500 truncate">{d.file_name} · {fmtBytes(d.file_size)}</div>
              </div>
              <div>
                <Badge className={`text-[10px] ${TYPE_COLOURS[d.document_type] || TYPE_COLOURS.other}`}>
                  {DOC_TYPES.find((t) => t.value === d.document_type)?.label || d.document_type}
                </Badge>
              </div>
              <div className="text-xs text-slate-500 truncate">
                {(d.tags || []).slice(0, 3).map((t) => <Badge key={t} className="mr-1 bg-slate-100 text-slate-600 text-[10px]">{t}</Badge>)}
              </div>
              <div className={`text-right text-xs ${expiryTone}`}>
                {d.expiry_date ? (<>{d.expiry_date}<br/>{dtil < 0 ? `${-dtil}d overdue` : dtil <= 90 ? `in ${dtil}d` : ""}</>) : "—"}
              </div>
              <div className="text-xs text-slate-500 truncate">
                {d.uploaded_at?.slice(0, 10)}<br/>
                <span className="text-slate-400">{d.uploaded_by}</span>
              </div>
              <div className="flex items-center gap-2 justify-end">
                <a data-testid={TID.docsDownload(d.id)}
                   href={d.storage_url} target="_blank" rel="noopener noreferrer"
                   className="text-slate-500 hover:text-gbe" title="Download">
                  <Download className="w-4 h-4" />
                </a>
                {canDelete && (
                  <button data-testid={TID.docsDelete(d.id)} onClick={() => onDelete(d.id)}
                          className="text-slate-500 hover:text-rose-700" title="Delete">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ------------- Upload -------------
function UploadDialog({ open, onOpenChange, selected, tree, onUploaded }) {
  const fileInput = useRef(null);
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({
    title: "",
    description: "",
    document_type: "other",
    tags: "",
    expiry_date: "",
    review_due_date: "",
  });
  const [busy, setBusy] = useState(false);

  // Attach to currently-selected tree node when opening
  useEffect(() => {
    if (open) {
      setFile(null);
      setForm({ title: "", description: "", document_type: "other", tags: "", expiry_date: "", review_due_date: "" });
    }
  }, [open]);

  // Derive attachment IDs from the current selection
  const attachment = useMemo(() => {
    const a = { client_id: null, site_id: null, building_id: null, area_id: null };
    if (!selected || !tree) return a;
    if (selected.kind === "client")   { a.client_id = selected.id; return a; }
    if (selected.kind === "site")     { a.site_id = selected.id; a.client_id = findParentClient(tree, "site", selected.id); return a; }
    if (selected.kind === "building") {
      const p = findParents(tree, "building", selected.id);
      a.building_id = selected.id; a.site_id = p.site_id; a.client_id = p.client_id; return a;
    }
    if (selected.kind === "area") {
      const p = findParents(tree, "area", selected.id);
      a.area_id = selected.id; a.building_id = p.building_id; a.site_id = p.site_id; a.client_id = p.client_id; return a;
    }
    return a;
  }, [selected, tree]);

  const submit = async () => {
    if (!file) { toast.error("Please choose a file"); return; }
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", form.title.trim());
      if (form.description) fd.append("description", form.description);
      fd.append("document_type", form.document_type);
      if (form.tags)             fd.append("tags", form.tags);
      if (form.expiry_date)      fd.append("expiry_date", form.expiry_date);
      if (form.review_due_date)  fd.append("review_due_date", form.review_due_date);
      if (attachment.client_id)   fd.append("client_id", attachment.client_id);
      if (attachment.site_id)     fd.append("site_id", attachment.site_id);
      if (attachment.building_id) fd.append("building_id", attachment.building_id);
      if (attachment.area_id)     fd.append("area_id", attachment.area_id);
      await api.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Uploaded "${form.title}"`);
      onUploaded();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Upload document</DialogTitle>
          <DialogDescription>
            {selected?.kind === "all"
              ? "Uploads without a tree selection are stored as unattached."
              : `Will attach to: ${selected.label}`}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-slate-700">File *</label>
            <input
              ref={fileInput}
              data-testid={TID.docsUploadFileInput}
              type="file"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="mt-1 block w-full text-sm text-slate-700 file:mr-2 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-gbe file:text-white file:hover:bg-gbe/90"
            />
            {file && <div className="text-xs text-slate-500 mt-1">{file.name} · {fmtBytes(file.size)}</div>}
          </div>
          <div>
            <label className="text-xs font-medium text-slate-700">Title *</label>
            <Input data-testid={TID.docsUploadTitle}
              value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="e.g. Chapel Hill Roof Inspection 2026-Q1" className="mt-1" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-700">Type</label>
              <Select value={form.document_type} onValueChange={(v) => setForm({ ...form, document_type: v })}>
                <SelectTrigger data-testid={TID.docsUploadType} className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DOC_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700">Tags (comma-sep)</label>
              <Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })}
                     placeholder="roof, asbestos" className="mt-1" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700">Expiry date</label>
              <Input data-testid={TID.docsUploadExpiry}
                type="date" value={form.expiry_date}
                onChange={(e) => setForm({ ...form, expiry_date: e.target.value })}
                className="mt-1" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700">Review due</label>
              <Input type="date" value={form.review_due_date}
                onChange={(e) => setForm({ ...form, review_due_date: e.target.value })}
                className="mt-1" />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-700">Description</label>
            <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                   placeholder="Optional notes" className="mt-1" />
          </div>
          <Badge className="bg-amber-100 text-amber-800 text-xs">
            Storage: local (SharePoint Graph switches on when Azure AD app registration lands)
          </Badge>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid={TID.docsUploadSubmit} onClick={submit} disabled={busy || !file || !form.title.trim()}
                  className="bg-gbe hover:bg-gbe/90 text-white">
            {busy ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Uploading…</> : "Upload"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ------------- tree helpers -------------
function findParentClient(tree, kind, id) {
  for (const c of tree.clients || []) {
    for (const s of c.sites || []) {
      if (kind === "site" && s.id === id) return c.id;
    }
  }
  return null;
}

function findParents(tree, kind, id) {
  const out = { client_id: null, site_id: null, building_id: null };
  for (const c of tree.clients || []) {
    for (const s of c.sites || []) {
      for (const b of s.buildings || []) {
        if (kind === "building" && b.id === id) {
          out.client_id = c.id; out.site_id = s.id; return out;
        }
        for (const a of b.areas || []) {
          if (kind === "area" && a.id === id) {
            out.client_id = c.id; out.site_id = s.id; out.building_id = b.id; return out;
          }
        }
      }
    }
  }
  return out;
}
